import React, { useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  Textarea,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import EmployeeDataService from "../../../services/Employee";
import { getUserAccess } from "../../../utils/authUtils";
import { HrmsPageShell } from "../../../components/Hrms/HrmsPageShell";

interface RoleRow {
  _id: string;
  name: string;
  description: string;
  isSystem: boolean;
  permissions: string[];
  isActive: boolean;
}

// Plain-language label + area for each permission key.
const PERMISSION_INFO: Record<string, { label: string; group: string }> = {
  "attendance.view.own": { label: "View own attendance", group: "Attendance" },
  "attendance.view.team": { label: "View team attendance", group: "Attendance" },
  "attendance.view.all": { label: "View everyone (attendance & leave)", group: "Attendance" },
  "attendance.correct.approve": { label: "Approve attendance corrections", group: "Attendance" },
  "leave.apply": { label: "Apply for leave", group: "Leave" },
  "leave.approve.level1": { label: "Approve leave (level 1)", group: "Leave" },
  "leave.approve.level2": { label: "Approve leave (level 2)", group: "Leave" },
  "dashboard.manager.view": { label: "Manager dashboard", group: "Dashboards & reports" },
  "dashboard.admin.view": { label: "Admin dashboard", group: "Dashboards & reports" },
  "reports.view": { label: "View reports", group: "Dashboards & reports" },
  "employees.manage": { label: "Manage employees", group: "Administration" },
  "config.manage": { label: "Configuration (holidays, policies, rules)", group: "Administration" },
  "roles.manage": { label: "Manage roles & permissions", group: "Administration" },
};
const PERMISSION_GROUPS = ["Attendance", "Leave", "Dashboards & reports", "Administration", "Other"];
const permissionInfo = (key: string) =>
  PERMISSION_INFO[key] || { label: key, group: "Other" };
const groupPermissions = (keys: string[]) =>
  PERMISSION_GROUPS.map((group) => ({
    group,
    keys: keys.filter((k) => permissionInfo(k).group === group),
  })).filter((g) => g.keys.length > 0);

const emptyForm = { name: "", description: "", permissions: [] as string[] };

export const RolesManagement = () => {
  const token = localStorage.getItem("logged_in_user_token");
  const userEmail = localStorage.getItem("logged_in_email") || "";
  const toast = useToast();
  const { canManageRoles } = getUserAccess();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [availablePermissions, setAvailablePermissions] = useState<string[]>([]);
  const [isNew, setIsNew] = useState(false);
  const [selectedId, setSelectedId] = useState<string>("");
  const [form, setForm] = useState(emptyForm);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [rolesRes, permsRes] = await Promise.all([
        EmployeeDataService.getRoles(token),
        EmployeeDataService.getAvailablePermissions(token),
      ]);
      if (rolesRes.data.status === "success") {
        setRoles(rolesRes.data.data || []);
      }
      if (permsRes.data.status === "success") {
        setAvailablePermissions(permsRes.data.data || []);
      }
    } catch (e: any) {
      console.error("Error fetching roles:", e);
    } finally {
      setIsLoading(false);
    }
  };

  const openEdit = (role?: RoleRow) => {
    if (role) {
      setSelectedId(role._id);
      setForm({
        name: role.name,
        description: role.description || "",
        permissions: role.permissions || [],
      });
      setIsNew(false);
    } else {
      setSelectedId("");
      setForm(emptyForm);
      setIsNew(true);
    }
    onOpen();
  };

  const handleSave = async () => {
    if (!form.name) {
      toast({
        position: "top",
        title: "Role name is required",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    setIsSaving(true);
    try {
      const res = isNew
        ? await EmployeeDataService.createRole(token, form, userEmail)
        : await EmployeeDataService.updateRole(
            token,
            selectedId,
            form,
            userEmail,
          );

      if (res.data.status === "success") {
        toast({
          position: "top",
          title: `Role ${isNew ? "created" : "updated"}`,
          status: "success",
          duration: 2000,
          isClosable: true,
        });
        onClose();
        fetchData();
      }
    } catch (e: any) {
      toast({
        position: "top",
        title: "Error",
        description: e.response?.data?.message || "Failed to save role.",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <HrmsPageShell
      title="Roles & Permissions"
      description="What each role can see and do in HRMS"
      sectionKey="configuration"
      noPermission={!canManageRoles}
      isLoading={canManageRoles && isLoading}
      actions={
        canManageRoles ? (
          <Button
            size="sm"
            bg="#327171"
            color="white"
            _hover={{ bg: "#285e5e" }}
            onClick={() => openEdit()}
          >
            Add Role
          </Button>
        ) : undefined
      }
    >
      {roles.length === 0 ? (
        <Text color="gray.500" textAlign="center" py={10}>
          No roles configured
        </Text>
      ) : (
        <SimpleGrid columns={{ base: 1, lg: 2, "2xl": 3 }} spacing={4}>
          {roles.map((role) => (
            <Box
              key={role._id}
              bg="white"
              border="1px solid #e2e8f0"
              borderRadius="12px"
              p={4}
            >
              <Flex justify="space-between" align="flex-start" gap={3}>
                <Box minW={0}>
                  <Flex align="center" gap={2}>
                    <Text fontSize="16px" fontWeight="700" color="#1d4747" m={0}>
                      {role.name}
                    </Text>
                    <Badge
                      colorScheme={role.isSystem ? "gray" : "purple"}
                      borderRadius="full"
                      px={2}
                      fontSize="9px"
                    >
                      {role.isSystem ? "System" : "Custom"}
                    </Badge>
                  </Flex>
                  {role.description && (
                    <Text fontSize="12px" color="gray.500" m={0} mt={0.5}>
                      {role.description}
                    </Text>
                  )}
                </Box>
                <Button
                  size="sm"
                  variant="outline"
                  borderColor="#327171"
                  color="#327171"
                  _hover={{ bg: "#327171", color: "white" }}
                  flexShrink={0}
                  onClick={() => openEdit(role)}
                >
                  Edit
                </Button>
              </Flex>
              <Text fontSize="11px" color="gray.500" m={0} mt={3} mb={1}>
                {role.permissions.length} permission{role.permissions.length === 1 ? "" : "s"}
              </Text>
              {groupPermissions(role.permissions).map(({ group, keys }) => (
                <Box key={group} mt={2}>
                  <Text
                    fontSize="10px"
                    fontWeight="700"
                    letterSpacing="0.06em"
                    textTransform="uppercase"
                    color="#327171"
                    m={0}
                    mb={1}
                  >
                    {group}
                  </Text>
                  <Flex wrap="wrap" gap={1.5}>
                    {keys.map((p) => (
                      <Badge
                        key={p}
                        title={p}
                        bg="#eef5f5"
                        color="#2d4f4f"
                        fontWeight="500"
                        textTransform="none"
                        fontSize="11px"
                        borderRadius="6px"
                        px={2}
                        py={0.5}
                      >
                        {permissionInfo(p).label}
                      </Badge>
                    ))}
                  </Flex>
                </Box>
              ))}
            </Box>
          ))}
        </SimpleGrid>
      )}

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {isNew ? "Add Role" : `Edit Role — ${form.name}`}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={3}>
              <FormLabel>Name</FormLabel>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
              />
            </FormControl>
            <FormControl>
              <FormLabel>Permissions</FormLabel>
              <CheckboxGroup
                value={form.permissions}
                onChange={(values) =>
                  setForm({ ...form, permissions: values as string[] })
                }
              >
                {groupPermissions(availablePermissions).map(({ group, keys }) => (
                  <Box key={group} mb={3}>
                    <Text fontSize="11px" fontWeight="700" textTransform="uppercase" color="#327171" m={0} mb={1}>
                      {group}
                    </Text>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={1.5}>
                      {keys.map((perm) => (
                        <Checkbox key={perm} value={perm} title={perm}>
                          <Text as="span" fontSize="13px">
                            {permissionInfo(perm).label}
                          </Text>
                        </Checkbox>
                      ))}
                    </SimpleGrid>
                  </Box>
                ))}
              </CheckboxGroup>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              bg="#327171"
              color="white"
              _hover={{ bg: "#285e5e" }}
              onClick={handleSave}
              isLoading={isSaving}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </HrmsPageShell>
  );
};
